import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import { useStore } from 'hooks';
import { groupBy } from 'helpers/utils';
import {
  ApplicationWagonService,
  WagonService,
  ProfileService,
  ContractService,
  StationService,
} from 'services';
import Success from './success';
import Form from './form';
import styles from '../order.module.scss';

const filterContracts = (value = []) => value.filter(({ contractProducts: c }) => c.length);

const useInitialValues = ({
  edit,
  wagons = [],
  profile = {},
  stations = [],
}) => {
  const {
    fio = '',
    email = '',
    phone = '',
  } = profile;

  const contracts = filterContracts(profile.contracts);

  const getValueObject = (data, key) => {
    if (data) {
      if (key in data) {
        if (Array.isArray(data[key]) && data[key].length) {
          return data[key][0];
        }
      }
    }
    return null;
  };

  const contract = contracts.length ? contracts[0] : null;
  const contractId = contract ? contract.id : '';
  const stationId = stations.length ? stations[0].id : '';
  const contractProduct = getValueObject(contract, 'contractProducts');
  const productGroup = contractProduct ? contractProduct.product.group : '';
  const wagonId = wagons.length ? wagons[0].id : '';

  const initialValues = {
    contractId: edit ? edit.contractId : contractId,
    productGroup: edit ? edit.productGroup : productGroup,
    userConsigneeId: edit ? edit.userConsigneeId : 'default',
    stationId: edit ? edit.stationId : stationId,
    fio: edit ? edit.user.fio : fio,
    email: edit ? edit.user.email : email,
    phone: edit ? edit.user.phone : phone,
    ton: edit ? edit.ton : 0.00.toFixed(2),
    wagonId: edit ? edit.wagonId : wagonId,
    wagonCount: edit ? edit.wagonCount : 0,
    file: null,
    gy12: edit ? edit.gy12 : '',
    type: edit ? edit.type : 0,
    utensils: edit ? edit.utensils : 0,
    codeGd: edit ? edit.codeGd : '',
  };

  if (edit) {
    initialValues.id = edit.id;
    initialValues.status = edit.status;
    initialValues.statusComment = '';
  }

  return initialValues;
};

const useFetch = ({ id, userId } = {}) => {
  const { setUser, setLoading } = useStore();
  const [tons, setTons] = useState([]);
  const [wagons, setWagons] = useState([]);
  const [profile, setProfile] = useState({});
  const [stations, setStations] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      const wagonResponse = await new WagonService({ setUser }).getWagons();
      if (wagonResponse) {
        setWagons(wagonResponse);
      }

      const profileResponse = await new ProfileService({ setUser }).getProfile({
        allContract: Number(!!id),
        userId,
      });
      if (profileResponse) {
        setProfile(profileResponse);
      }

      const tonsResponse = await new ContractService({ setUser }).getTons({ userId });
      if (tonsResponse) {
        setTons(tonsResponse);
      }

      const stationResponse = await new StationService({ setUser }).getStations();
      if (stationResponse) {
        setStations(stationResponse);
      }

      if (id) {
        const statusHistoryResponse = await new ApplicationWagonService({ setUser })
          .getStatusHistory({ id });
        if (statusHistoryResponse) {
          setStatusHistory(statusHistoryResponse);
        }
      }

      setLoading(false);
    })();
  }, [id, userId, setUser, setLoading]);

  return [tons, wagons, profile, stations, statusHistory];
};

const Wagon = ({ edit, onClose }) => {
  const [tons, wagons, profile, stations, statusHistory] = useFetch(edit);
  const initialValues = useInitialValues({ edit, wagons, profile, stations });
  const [currentContractBalance, setCurrentContractBalance] = useState(0);
  const { setUser, setLoading } = useStore();
  const [order, setOrder] = useState(null);
  const isSubmittingRef = useRef(false);
  const { userConsignees = [] } = profile;
  const contracts = filterContracts(profile.contracts);

  useEffect(() => {
    setCurrentContractBalance(contracts.find(el => el.id === initialValues.contractId)?.balance);
  }, [contracts, initialValues.contractId]);

  const getIndex = (data, value) => data.findIndex(({ id }) => id === Number(value));

  const onChangeContract = ({ target: { value } }, setFieldValue) => {
    const contractIndex = getIndex(contracts, value);

    setFieldValue('productGroup', '');

    if (contractIndex >= 0) {
      const { contractProducts = [] } = contracts[contractIndex];
      if (contractProducts.length) {
        setFieldValue('productGroup', contractProducts[0].product.group);
      }
    }
    setCurrentContractBalance(contracts[contractIndex].balance);
    setFieldValue('ton', 0);
  };

  const onChangeType = (e, setFieldValue) => {
    setFieldValue('ton', 0);
  };

  const getCountWagon = ({ wagonId, ton }) => {
    const wagonCurrent = groupBy(wagons, 'id')[wagonId];
    return Array.isArray(wagonCurrent) && wagonCurrent.length
      ? Math.round(ton / wagonCurrent[0].ton)
      : 0;
  };

  const onSubmit = async (values) => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    const applicationWagonService = new ApplicationWagonService({ setUser });
    const formData = new FormData();
    const keys = Object.keys(values);

    keys.forEach((key) => {
      const item = values[key];
      if (edit) {
        switch (key) {
          case 'id':
          case 'status':
          case 'file':
          case 'gy12':
            formData.append(key, item);
            break;
          case 'statusComment':
            formData.append('comment', item);
            break;
          default:
        }
      } else {
        switch (key) {
          case 'wagonCount':
            formData.append(key, getCountWagon(values));
            break;
          case 'userConsigneeId':
            formData.append(key, item === 'default' ? null : item);
            break;
          default:
            formData.append(key, item);
            break;
        }
      }
    });

    setLoading(true);

    try {
      if (edit) {
        const response = await applicationWagonService.changeStatus(formData);
        if (response) {
          toast.success('Заявка обновлена');
        }
        onClose(true);
      } else {
        const response = await applicationWagonService.postApplication(formData);
        if (response && response.id) {
          setOrder(response);
        }
      }
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <section className={classnames(styles.order, { [styles.success]: order !== null })}>
      {order ? (
        <Success
          order={order}
          onClose={onClose}
        />
      ) : (
        <Form
          currentContractBalance={currentContractBalance}
          edit={edit}
          tons={tons}
          wagons={wagons}
          profile={profile}
          stations={stations}
          contracts={contracts}
          statusHistory={statusHistory}
          userConsignees={userConsignees}
          initialValues={initialValues}
          onClose={onClose}
          onSubmit={onSubmit}
          onChangeContract={onChangeContract}
          onChangeType={onChangeType}
          getCountWagon={getCountWagon}
          getIndex={getIndex}
        />
      )}
    </section>
  );
};

export default Wagon;
